import React, { useState } from 'react';
import { Container, Card, Row as BootstrapRow, Col as BootstrapCol, Button } from 'react-bootstrap';
import { Table, Select, Modal, Form } from 'antd';
import { FilterOutlined, PlusOutlined, EditOutlined } from '@ant-design/icons';
import FormItem from '../common/FormItem/FormItem';
import PageTitle from '../common/PageTitle/PageTitle';
import { withAuthenticationRequired } from '@auth0/auth0-react';
import { getChannelArtwork } from '@/utils/artwork';
import styles from './pricing-devices.module.scss';

interface Device {
  id: string;
  name: string;
  description: string;
  price: number;
  category: 'player' | 'accessory';
  status: 'active' | 'inactive';
  stockStatus: 'in-stock' | 'low-stock' | 'out-of-stock';
  artwork?: string;
}

const PricingDevices: React.FC = () => {
  const [showDeviceModal, setShowDeviceModal] = useState(false);
  const [editingDevice, setEditingDevice] = useState<Device | null>(null);
  const [filters, setFilters] = useState({
    deviceName: undefined as string | undefined,
    category: undefined as string | undefined,
    status: undefined as string | undefined,
    stockStatus: undefined as string | undefined
  });
  const [resetCounter, setResetCounter] = useState(0);

  // Mock data for devices (alphabetized)
  const [devices, setDevices] = useState<Device[]>([
    { 
      id: '3', 
      name: 'KrossKast Adapter Female', 
      description: 'Audio adapter cable for RCA to 3.5mm connection',
      price: 5.00,
      category: 'accessory',
      status: 'active',
      stockStatus: 'low-stock'
    },
    { 
      id: '2', 
      name: 'KrossKast Adapter Male', 
      description: 'Audio adapter cable for RCA to 3.5mm connection',
      price: 5.00,
      category: 'accessory',
      status: 'active',
      stockStatus: 'in-stock'
    },
    { 
      id: '4', 
      name: 'KrossKast Hanging bracket', 
      description: 'Wall mounting bracket for KrossKast Player',
      price: 14.00,
      category: 'accessory',
      status: 'active',
      stockStatus: 'in-stock'
    },
    { 
      id: '1', 
      name: 'KrossKast Player 8', 
      description: 'Professional music player device',
      price: 165.00,
      category: 'player',
      status: 'active',
      stockStatus: 'in-stock'
    },
    { 
      id: '5', 
      name: 'KrossKast Player 11', 
      description: 'Advanced music player device with enhanced features',
      price: 299.00,
      category: 'player',
      status: 'inactive',
      stockStatus: 'out-of-stock'
    }
  ]);

  // Filter options
  const filterOptions = {
    deviceName: devices.map(device => ({ value: device.name, label: device.name })),
    category: [
      { value: 'player', label: 'Player' },
      { value: 'accessory', label: 'Accessory' }
    ],
    status: [
      { value: 'active', label: 'Active' },
      { value: 'inactive', label: 'Inactive' }
    ],
    stockStatus: [
      { value: 'in-stock', label: 'In Stock' },
      { value: 'low-stock', label: 'Low Stock' },
      { value: 'out-of-stock', label: 'Out of Stock' }
    ]
  };

  // Filter the devices based on current filters (always alphabetically sorted)
  const filteredDevices = devices.filter(device => {
    if (filters.deviceName && !device.name.toLowerCase().includes(filters.deviceName.toLowerCase())) return false;
    if (filters.category && device.category !== filters.category) return false;
    if (filters.status && device.status !== filters.status) return false;
    if (filters.stockStatus && device.stockStatus !== filters.stockStatus) return false;
    return true;
  }).sort((a, b) => a.name.localeCompare(b.name));

  const handleFilterChange = (filterName: string, value: string | undefined) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  const handleReset = () => {
    setFilters({ deviceName: undefined, category: undefined, status: undefined, stockStatus: undefined });
    setResetCounter(prev => prev + 1);
  };

  const handleSaveDevice = () => {
    console.log('Saving device...');
    setShowDeviceModal(false);
    setEditingDevice(null);
  };

  const handleEditDevice = (device: Device) => {
    setEditingDevice(device);
    setShowDeviceModal(true);
  };

  const handleCreateDevice = () => {
    setEditingDevice(null);
    setShowDeviceModal(true);
  };

  // Helper functions to maintain alphabetical order
  const handleUpdateDevice = (updatedDevice: Device) => {
    setDevices(prevDevices => 
      prevDevices
        .map(device => device.id === updatedDevice.id ? updatedDevice : device)
        .sort((a, b) => a.name.localeCompare(b.name))
    );
  };

  const handleAddDevice = (newDevice: Device) => {
    setDevices(prevDevices => 
      [...prevDevices, newDevice].sort((a, b) => a.name.localeCompare(b.name))
    );
  };

  return (
    <Container fluid>
      <Card>
        <Card.Body>
          <Card.Title style={{ display: 'flex', justifyContent: 'space-between' }}>
            <PageTitle title="Pricing Management Devices & Accessories" />
            <Button 
              onClick={handleReset}
              style={{ 
                background: 'white', 
                color: '#333', 
                border: '1px solid #e0e0e0', 
                borderRadius: '6px',
                padding: '8px 16px',
                fontWeight: '500'
              }}
            >
              <FilterOutlined /> Reset All
            </Button>
          </Card.Title>
          
          <Form layout="vertical" className={styles.form}>
            <BootstrapRow>
              <BootstrapCol>
                <BootstrapRow className="mb-2">
                  <BootstrapCol>
                    <BootstrapRow>
                      <BootstrapCol md={4}>
                        <FormItem
                          label="Device Name"
                          className="customSelectWrapper"
                          layout="vertical"
                        >
                          <Select
                            key={`deviceName-reset-${resetCounter}`}
                            showSearch
                            allowClear
                            className={`customSelect ${styles.selectField}`}
                            placeholder="Any device name"
                            value={filters.deviceName}
                            onChange={value => handleFilterChange('deviceName', value)}
                            options={filterOptions.deviceName}
                          />
                        </FormItem>
                      </BootstrapCol>
                      <BootstrapCol md={4}>
                        <FormItem
                          label="Category"
                          className="customSelectWrapper"
                          layout="vertical"
                        >
                          <Select
                            key={`category-reset-${resetCounter}`}
                            allowClear
                            className={`customSelect ${styles.selectField}`}
                            placeholder="Any category"
                            value={filters.category}
                            onChange={value => handleFilterChange('category', value)}
                            options={filterOptions.category}
                          />
                        </FormItem>
                      </BootstrapCol>
                      <BootstrapCol md={4}>
                        <FormItem
                          label="Status"
                          className="customSelectWrapper"
                          layout="vertical"
                        >
                          <Select
                            key={`status-reset-${resetCounter}`}
                            allowClear
                            className={`customSelect ${styles.selectField}`}
                            placeholder="Any status"
                            value={filters.status}
                            onChange={value => handleFilterChange('status', value)}
                            options={filterOptions.status}
                          />
                        </FormItem>
                      </BootstrapCol>
                    </BootstrapRow>
                  </BootstrapCol>
                </BootstrapRow>
                <BootstrapRow className="mb-2">
                  <BootstrapCol>
                    <BootstrapRow>
                      <BootstrapCol md={4}>
                        <FormItem
                          label="Stock Status"
                          className="customSelectWrapper"
                          layout="vertical"
                        >
                          <Select
                            key={`stockStatus-reset-${resetCounter}`}
                            allowClear
                            className={`customSelect ${styles.selectField}`}
                            placeholder="Any stock status"
                            value={filters.stockStatus}
                            onChange={value => handleFilterChange('stockStatus', value)}
                            options={filterOptions.stockStatus}
                          />
                        </FormItem>
                      </BootstrapCol>
                      <BootstrapCol 
                        md={8}
                        className="d-flex align-items-center mt-2 mt-md-0 gap-3"
                      >
                        <Button
                          style={{ 
                            background: 'white', 
                            color: '#e21e26', 
                            border: '1px solid #e21e26', 
                            borderRadius: '6px',
                            padding: '8px 16px',
                            fontWeight: '500',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            justifyContent: 'center',
                            height: '50px',
                            marginTop: '8px',
                            width: '200px'
                          }}
                          onClick={handleCreateDevice}
                        >
                          <PlusOutlined style={{ fontSize: 16 }} />
                          Add New Device
                        </Button>
                        <Button
                          style={{ 
                            background: 'white', 
                            color: '#e21e26', 
                            border: '1px solid #e21e26', 
                            borderRadius: '6px',
                            padding: '8px 16px',
                            fontWeight: '500',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            justifyContent: 'center',
                            height: '50px',
                            marginTop: '8px',
                            width: '200px'
                          }}
                          onClick={handleCreateDevice}
                        >
                          <PlusOutlined style={{ fontSize: 16 }} />
                          Add New Accessory
                        </Button>
                      </BootstrapCol>
                    </BootstrapRow>
                  </BootstrapCol>
                </BootstrapRow>
              </BootstrapCol>
            </BootstrapRow>
          </Form>
          
          {/* Devices Grid */}
          <BootstrapRow>
            {filteredDevices.map((device) => (
              <BootstrapCol md={6} key={device.id} className="mb-3">
                <Card className={styles.deviceCard}>
                  <Card.Body>
                    <div className={styles.deviceContent}>
                      <div className={styles.deviceDetails}>
                        <div className={styles.deviceHeader}>
                          <h5>{device.name}</h5>
                        </div>
                        <p className={styles.deviceDescription}>{device.description}</p>
                        <div className={styles.deviceInfo}>
                          <div className={styles.infoRow}>
                            <span className={styles.priceLabel}>Price:</span>
                            <span className={styles.price}>${device.price.toFixed(2)}</span>
                          </div>
                          <div className={styles.infoRow}>
                            <span>Status:</span>
                            <span className={`${styles.statusBadge} ${styles[device.status]}`}>
                              {device.status === 'active' ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                          <div className={styles.infoRow}>
                            <span>Stock:</span>
                            <span className={`${styles.stockBadge} ${styles[device.stockStatus]}`}>
                              {device.stockStatus === 'in-stock' ? 'In Stock' : 
                               device.stockStatus === 'low-stock' ? 'Low Stock' : 'Out of Stock'}
                            </span>
                          </div>
                          <div className={styles.infoRow}>
                            <span>Category:</span>
                            <span className={`${styles.categoryBadge} ${styles[device.category]}`}>
                              {device.category === 'player' ? 'Player' : 'Accessory'}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className={styles.deviceArtwork}>
                        <div className={styles.artworkContainer}>
                          <img 
                            src={getChannelArtwork(device.artwork)} 
                            alt={device.name} 
                            className={styles.artwork}
                          />
                        </div>
                      </div>
                    </div>
                    <Button 
                      variant="link" 
                      size="sm"
                      onClick={() => handleEditDevice(device)}
                      className={styles.editButton}
                    >
                      <EditOutlined />
                    </Button>
                  </Card.Body>
                </Card>
              </BootstrapCol>
            ))}
          </BootstrapRow>

          {filteredDevices.length === 0 && (
            <div className={styles.noResults}>
              <p>No devices found matching your filters.</p>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Device Modal */}
      <Modal
        open={showDeviceModal}
        onCancel={() => setShowDeviceModal(false)}
        onOk={handleSaveDevice}
        okText={editingDevice ? 'Update Device' : 'Add Device'}
        title={editingDevice ? 'Edit Device' : 'Add New Device'}
        width={600}
      >
        <div>
          <p>Device form would go here.</p>
          <p>This is a placeholder for the device creation/editing functionality.</p>
        </div>
      </Modal>
    </Container>
  );
};

export default withAuthenticationRequired(PricingDevices);
